import type { ButtonHTMLAttributes, CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The few primitives the page is built from, dense by default: 28px controls, 12px text,
 * hairline borders. A touch target is still at least 28px tall, which with the gap
 * between controls clears a fingertip.
 */

export function Card({ title, actions, className, children }: { title?: ReactNode; actions?: ReactNode; className?: string; children: ReactNode }) {
  return (
    <section className={cn('min-w-0 rounded-md border border-line bg-surface', className)}>
      {title !== undefined && (
        <header className="flex min-h-8 items-center justify-between gap-2 border-b border-line px-2.5 py-1">
          <h2 className="truncate text-xs font-semibold tracking-wide text-ink-2 uppercase">{title}</h2>
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

type ButtonVariant = 'primary' | 'ghost' | 'outline';

export function Button({ variant = 'outline', className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-7 items-center justify-center gap-1.5 rounded-md px-2.5 text-xs font-medium whitespace-nowrap transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'primary' && 'bg-accent text-accent-ink hover:opacity-90',
        variant === 'outline' && 'border border-line bg-surface text-ink hover:bg-surface-2',
        variant === 'ghost' && 'text-ink-2 hover:bg-surface-2 hover:text-ink',
        className,
      )}
      {...props}
    />
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        'h-7 w-full min-w-0 rounded-md border border-line bg-page px-2 text-xs text-ink tabular placeholder:text-muted',
        'focus-visible:border-accent focus-visible:outline-none',
        className,
      )}
      {...props}
    />
  );
}

export function Field({ label, hint, children, className }: { label: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn('flex min-w-0 flex-col gap-0.5', className)}>
      <span className="text-[11px] text-ink-2">
        {label}
        {hint !== undefined && <span className="ml-1 text-muted">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

export interface SegmentOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

/** A radio group drawn as joined buttons. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: ReadonlyArray<SegmentOption<T>>;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex h-7 rounded-md border border-line bg-page p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn('rounded-sm px-2.5 text-xs font-semibold transition-colors', option.value === value ? 'bg-accent text-accent-ink' : 'text-ink-2 hover:text-ink')}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <span aria-hidden className={cn('inline-block size-3 animate-spin rounded-full border-2 border-line border-t-accent', className)} />;
}

export function Message({ tone = 'muted', children }: { tone?: 'muted' | 'error'; children: ReactNode }) {
  return <div className={cn('px-2.5 py-3 text-xs', tone === 'error' ? 'text-negative' : 'text-muted')}>{children}</div>;
}

/** A marker in an outcome's shape, for legends and tables, so identity never rests on colour alone. */
export function OutcomeMarker({ color, shape, size = 8 }: { color: string; shape: 'circle' | 'diamond' | 'square' | 'ring'; size?: number }) {
  const r = size / 2;
  return (
    <svg width={size} height={size} viewBox={`${-r} ${-r} ${size} ${size}`} aria-hidden className="shrink-0 overflow-visible">
      <MarkerShape color={color} shape={shape} r={r} />
    </svg>
  );
}

export function MarkerShape({ color, shape, r }: { color: string; shape: 'circle' | 'diamond' | 'square' | 'ring'; r: number }) {
  switch (shape) {
    case 'circle':
      return <circle r={r} fill={color} />;
    case 'ring':
      return <circle r={r - 0.75} fill="none" stroke={color} strokeWidth={1.5} />;
    case 'square':
      return <rect x={-r * 0.85} y={-r * 0.85} width={r * 1.7} height={r * 1.7} rx={1} fill={color} />;
    case 'diamond':
      return <path d={`M0 ${-r * 1.15} L${r * 1.15} 0 L0 ${r * 1.15} L${-r * 1.15} 0 Z`} fill={color} />;
  }
}

/**
 * A placeholder block the size of what is loading, so the page is laid out before its data
 * arrives and nothing below it jumps when it does. Hidden from assistive technology: the
 * container a skeleton stands in carries the `role="status"` that says what is loading.
 */
export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <span aria-hidden className={cn('block animate-pulse rounded-sm bg-surface-2', className)} style={style} />;
}
